import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {v4 as uuidv4} from 'uuid';

import isArchived from 'archive/isArchived';
import {Owner} from 'auth/PermissionConstants';
import {EntitySummary} from 'constants/UserConstants';
import SchemaOwnerDropdown from 'results/assay-schemas/SchemaOwnerDropdown';
import {ResultSchemaSelectorIdInput} from 'results/ResultSchemaSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import SettingsField from 'ui-library/SettingsField';

import {TestDefinitionForEditor} from './TestDefinitionEditor';

const MAX_STRING_LENGTH = 255;

function Row({children}: {children: JSX.Element}): JSX.Element {
  return <div className={css(styles.testDefinitionMetadataRow)}>{children}</div>;
}

function Col({children}: {children: JSX.Element}): JSX.Element {
  return <div className={css(styles.testDefinitionMetadataColumn)}>{children}</div>;
}

function FieldWrapper({children}: {children: JSX.Element}): JSX.Element {
  return <div className={css(styles.testDefinitionMetadataField)}>{children}</div>;
}

export interface TestDefinitionMetadataSettingsProps {
  ownerId: string | null;
  setOwnerId: (id: string) => void;
  name: string;
  setName: (id: string) => void;
  description: string;
  setDescription: (id: string) => void;
  resultSchemaId: string | null;
  setResultSchemaId: (id: string) => void;
  handleNext: () => void;
  ownerChoices: Array<Owner>;
  testDefinition?: TestDefinitionForEditor;
}

/**
 * Presentational component for configuring a TestDefinition.
 */
export default function TestDefinitionMetadataSettings({
  ownerId,
  setOwnerId,
  name,
  setName,
  description,
  setDescription,
  resultSchemaId,
  setResultSchemaId,
  handleNext,
  ownerChoices,
  testDefinition,
}: TestDefinitionMetadataSettingsProps): JSX.Element {
  let error = null;
  if (!name || name.trim() === '') {
    error = 'Test definition needs a name';
  } else if (!resultSchemaId) {
    error = 'Test definition needs a result schema';
  } else if (ownerId == null) {
    error = 'Test definition needs an owner';
  }

  const isCreatingTestDefinition = testDefinition == null;

  return (
    <div className="u-flex-grow u-min-width-zero">
      <div className="settingsPageContainer-section">
        {/* TODO(BNCH-62078) add SQL identifier / warehouse name when we add warehouse support */}
        <Row>
          <>
            <Col>
              {/** Modeled off of SchemaNameInput, to avoid polluting with non-schema model */}
              <SettingsField
                isBeingCreated={isCreatingTestDefinition}
                isRequired={true}
                isWritable={true}
                label="Name"
                name="name"
                maxLength={MAX_STRING_LENGTH}
                onChange={setName}
                value={name}
                dataTestAttrs={{component: 'TestDefinitionMetadataSettings', element: 'nameInput'}}
              />
            </Col>
            <Col>
              <FieldWrapper>
                <SettingsField
                  isBeingCreated={isCreatingTestDefinition}
                  isRequired={false}
                  isWritable={true}
                  label="Description"
                  name="description"
                  maxLength={MAX_STRING_LENGTH}
                  onChange={setDescription}
                  value={description}
                  dataTestAttrs={{component: 'TestDefinitionMetadataSettings', element: 'descriptionInput'}}
                />
              </FieldWrapper>
            </Col>
            <Col>
              <FieldWrapper>
                {/** Modeled off of SchemaOwnerDisplay, to avoid polluting with non-schema model */}
                {isCreatingTestDefinition ? (
                  <SchemaOwnerDropdown
                    ownerChoices={ownerChoices}
                    className={css(styles.ownerDropdown)}
                    owner={ownerChoices.find((owner) => owner.id === ownerId) ?? null}
                    onOwnerChanged={(owner: EntitySummary) => {
                      setOwnerId(owner.id);
                    }}
                    ownerLabel="Owner"
                    noChoicesMessage="You can only create test definitions for organizations where you’re an admin"
                  />
                ) : (
                  <InputWrapper id={uuidv4()} label="Owner" labelStyle={FieldLabel.STYLES.LABEL}>
                    <span
                      className="settingsPageContainer-readOnlyValue"
                      {...getTestProps({
                        component: 'TestDefinitionMetadataSettings',
                        element: 'ownerName',
                      })}
                    >
                      {testDefinition.organization.name}
                    </span>
                  </InputWrapper>
                )}
              </FieldWrapper>
            </Col>
          </>
        </Row>
        <FieldLabel isRequired={true} label="Result Schema" />
        <ResultSchemaSelectorIdInput
          selectedSchemaId={resultSchemaId}
          onChange={(selectedResultSchema) => {
            setResultSchemaId(selectedResultSchema?.id ?? '');
          }}
          // Filter out archived result schemas, unless that schema was previously selected. Archived, selected
          // schemas are included so the user can see the current selection before updating.
          filter={(resultSchema) => !isArchived(resultSchema) || resultSchema.id === resultSchemaId}
        />
      </div>
      {isCreatingTestDefinition && (
        <TooltipWrapper tooltipTitle={error}>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={handleNext}
            size={ButtonSize.MEDIUM}
            text="Next"
            disabled={error != null}
            dataTestAttrs={{component: 'TestDefinitionMetadataSettings', element: 'nextButton'}}
          />
        </TooltipWrapper>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  settingsGroup: {
    marginTop: '32px',
    marginBottom: '32px',
  },
  testDefinitionMetadataRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '32px',
  },
  testDefinitionMetadataColumn: {
    flex: 0.3,
    minWidth: 0,
  },
  testDefinitionMetadataField: {
    width: 'auto',
    marginBottom: '0',
  },
  ownerDropdown: {
    flex: 1,
  },
});
